import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import { STORAGE_KEYS } from '../utils/constants';
import { StorageService } from '../shared/storage.service';
import { environment } from 'src/environments/environment';

interface ZendeskConfig {
  enabled: boolean;
  widgetScriptUrl?: string;
  username?: string;
  email?: string;
}

declare global {
  interface Window {
    zE?: any;
    zESettings?: any;
  }
}

@Injectable({ providedIn: 'root' })
export class ZendeskService {
  private config: ZendeskConfig = {
    enabled: true,
    widgetScriptUrl:
      'https://static.zdassets.com/ekr/snippet.js?key=598a73e8-7172-40d9-bf9a-e35a87c75eb9'
  };

  // Desired launcher visibility. Tracked separately so callers (e.g. routes
  // with our own floating chat) can request "hide" before the Zendesk script
  // has finished loading; we reapply the latest intent once the widget is
  // ready inside the script's onload callback.
  private hidden = false;

  // Callbacks invoked when the Zendesk widget is opened. Registered by
  // callers (e.g. the floating hint bubble) so they can react to the user
  // engaging the widget. Kept here because the 'open' listener can only be
  // attached once the Zendesk script has loaded.
  private openHandlers: Array<() => void> = [];

  // Guard so the Zendesk 'open' listener is attached only once per page load.
  // setupZendeskAuth() may run again (e.g. initialize() called on login /
  // session restore); without this we'd stack duplicate listeners and fire
  // sendUserDataToZendesk()/openHandlers multiple times per open.
  private openListenerAttached = false;

  constructor(
    private authService: AuthService,
    private storageService: StorageService
  ) {}

  /**
   * Inicializa el widget de Zendesk y configura la autenticación JWT
   * @param config Configuración opcional para sobrescribir los valores predeterminados
   */
  initialize(config?: Partial<ZendeskConfig>) {
    if (config) {
      this.config = { ...this.config, ...config };
    }

    if (!this.config.enabled) {
      return;
    }

    // Habilitar Zendesk en development y production
    this.loadZendeskWidget();
  }

  /**
   * Carga el script del widget de Zendesk
   */
  private loadZendeskWidget() {
    if (typeof window === 'undefined' || !this.config.widgetScriptUrl) {
      return;
    }

    // Si el script ya está cargado, no lo carguemos de nuevo
    if (document.getElementById('ze-snippet')) {
      this.setupZendeskAuth();
      return;
    }

    const script = document.createElement('script');
    script.id = 'ze-snippet';
    script.src = this.config.widgetScriptUrl;
    script.async = true;
    script.onload = () => {
      this.setupZendeskAuth();
      window.zE('messenger:set', 'locale', 'es');
      window.zE('messenger:set', 'conversationTags', ['superpay23', 'no_auth']);
      // Reapply any hide/show requested before the script finished loading.
      this.applyVisibility();
    };

    document.head.appendChild(script);
  }

  private emailToTag(email: string) {
    return 'email_' + email.replace(/[@.]/g, '_');
  }
  private sendUserDataToZendesk() {
    if (this.config.username === undefined || this.config.email === undefined) {
      return;
    }

    const email = this.emailToTag(this.config.email);
    window.zE('messenger:set', 'conversationFields', [
      { id: '37632638629915', value: `${this.config.username}` }
    ]);
    window.zE('messenger:set', 'conversationFields', [
      { id: '37632800861211', value: `${this.config.email}` }
    ]);
    window.zE('messenger:set', 'conversationTags', [
      `auth_${this.config.username}`,
      `${email}`,
      'superpay23'
    ]);
  }

  /**
   * Configura la autenticación con Zendesk usando JWT
   */
  private setupZendeskAuth() {
    if (typeof window === 'undefined' || !window.zE) {
      return;
    }

    // Attach the 'open' listener only once — subsequent setupZendeskAuth()
    // calls would otherwise duplicate it.
    if (this.openListenerAttached) {
      return;
    }
    this.openListenerAttached = true;

    // Configurar autenticación cuando el widget esté listo
    window.zE('messenger:on', 'open', () => {
      // this.authenticateUser();
      this.sendUserDataToZendesk();
      this.openHandlers.forEach((cb) => cb());
    });
  }

  /**
   * Registra un callback que se ejecuta cuando el usuario abre el widget de
   * Zendesk. Útil para descartar el globo "¿Necesitas ayuda?".
   * @returns Función para desuscribir el callback (llamar en ngOnDestroy).
   */
  onOpen(cb: () => void): () => void {
    this.openHandlers.push(cb);
    return () => {
      this.openHandlers = this.openHandlers.filter((h) => h !== cb);
    };
  }

  /**
   * Autentica al usuario en Zendesk
   */
  authenticateUser() {
    if (this.storageService.get(STORAGE_KEYS.AUTH_TOKEN)) {
      this.authService.authenticateZendesk().then((success) => {
        if (!success) {
          console.warn('No se pudo autenticar en Zendesk');
        }
      });
    }
  }

  logoutUser() {
    if (typeof window !== 'undefined' && window.zE) {
      // window.zE('webWidget', 'logout');
      window.zE('messenger', 'logoutUser');
      console.log('Logout Zendesk');
    }
  }

  /**
   * Abre el widget de Zendesk
   */
  openZendeskWidget() {
    if (typeof window !== 'undefined' && window.zE) {
      window.zE('messenger', 'open');
    }
  }

  /**
   * Cierra el widget de Zendesk
   */
  closeZendeskWidget() {
    if (typeof window !== 'undefined' && window.zE) {
      window.zE('messenger', 'close');
    }
  }

  /**
   * Oculta el launcher flotante de Zendesk. Útil en rutas con nuestro propio
   * chat (p.ej. /fund-account/crypto) para que ambos no compitan en pantalla.
   */
  hide() {
    this.hidden = true;
    this.applyVisibility();
  }

  /**
   * Vuelve a mostrar el launcher de Zendesk si fue ocultado.
   */
  show() {
    this.hidden = false;
    this.applyVisibility();
  }

  private applyVisibility() {
    if (typeof window === 'undefined' || !window.zE) return;
    try {
      window.zE('messenger', this.hidden ? 'hide' : 'show');
    } catch {
      // Widget aún no inicializado del todo — se reaplica desde script.onload.
    }
  }

  /**
   * Establece la preferencia de idioma para el widget
   * @param locale Código de idioma (ej. 'es', 'en-US')
   */
  setLocale(locale: string) {
    if (typeof window !== 'undefined' && window.zE) {
      window.zE('messenger', 'setLocale', locale);
    }
  }
}
